// src/pages/BadgeDesignerPage.js — design the name tag: drag fields, fonts, sizes, logo, label size
// [marker: back-tags v1] [marker: tag-art v1]
// /badges            the default design for every event
// /events/:id/badges this event's design (starts from the default)
import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useParams, Link } from 'react-router-dom';
import api from '../utils/api';
import toast from 'react-hot-toast';
import { useAuth } from '../context/AuthContext';
import { PRESETS, FIELDS, FONTS, SAMPLE, defaultDesign, BadgeView, printBadges, sizeOf, newId } from '../components/Badge';

const clamp = (v, a, b) => Math.max(a, Math.min(b, v));
const r1 = (v) => Math.round(v * 10) / 10;

export default function BadgeDesignerPage() {
  const { id: eventId } = useParams();
  const { isManagerOrAbove } = useAuth();
  const canEdit = isManagerOrAbove();
  const [info, setInfo] = useState(null);
  const [design, setDesign] = useState(null);
  const [selected, setSelected] = useState(null);
  const [dirty, setDirty] = useState(false);
  const [saving, setSaving] = useState(false);
  const [people, setPeople] = useState([]);
  const [previewPerson, setPreviewPerson] = useState('sample');
  const boxRef = useRef(null);
  const drag = useRef(null);
  const [scale, setScale] = useState(1.4);
  const [artBusy, setArtBusy] = useState(false); // [marker: tag-art v1]
  const [artFit, setArtFit] = useState(null);    // size of the last uploaded artwork, to offer a matching tag size

  const load = useCallback(() => {
    api.get(`/badges/design${eventId ? `?eventId=${eventId}` : ''}`).then(r => {
      setInfo(r.data);
      setDesign(r.data.design || defaultDesign(r.data.orgLogoUrl));
      setDirty(false);
    }).catch(() => toast.error('Could not load the name tag design.'));
    if (eventId) api.get(`/badges/events/${eventId}/people`).then(r => setPeople(r.data || [])).catch(() => {});
  }, [eventId]);
  useEffect(() => { load(); }, [load]);

  // fit the preview to the screen
  useEffect(() => {
    const fit = () => {
      if (!design) return;
      const { w, h } = sizeOf(design);
      const avail = Math.min(window.innerWidth - 520, 760);
      setScale(clamp(Math.min(avail / (w * 96), 520 / (h * 96)), 0.6, 2.2));
    };
    fit(); window.addEventListener('resize', fit); return () => window.removeEventListener('resize', fit);
  }, [design]);

  const update = (fn) => { setDesign(d => fn(JSON.parse(JSON.stringify(d)))); setDirty(true); };
  const setField = (id, k, v) => update(d => { const f = d.fields.find(x => x.id === id); if (f) f[k] = v; return d; });

  // drag elements around the tag
  const onPointerDownItem = (e, id) => {
    if (!canEdit) return;
    e.preventDefault(); e.stopPropagation();
    setSelected(id);
    const rect = boxRef.current.getBoundingClientRect();
    const item = id === 'logo' ? design.logo : design.fields.find(f => f.id === id);
    drag.current = { id, startX: e.clientX, startY: e.clientY, x0: item.x, y0: item.y, w: rect.width, h: rect.height };
    e.target.setPointerCapture && e.target.setPointerCapture(e.pointerId);
  };
  useEffect(() => {
    const move = (e) => {
      const d = drag.current; if (!d) return;
      const dx = ((e.clientX - d.startX) / d.w) * 100; const dy = ((e.clientY - d.startY) / d.h) * 100;
      setDesign(cur => {
        const n = JSON.parse(JSON.stringify(cur));
        const it = d.id === 'logo' ? n.logo : n.fields.find(f => f.id === d.id);
        if (it) { it.x = r1(clamp(d.x0 + dx, -5, 100 - Math.min(it.w, 95))); it.y = r1(clamp(d.y0 + dy, -5, 98)); }
        return n;
      });
      setDirty(true);
    };
    const up = () => { drag.current = null; };
    window.addEventListener('pointermove', move); window.addEventListener('pointerup', up);
    return () => { window.removeEventListener('pointermove', move); window.removeEventListener('pointerup', up); };
  }, []);

  if (!design || !info) return <div className="loading-page"><div className="spinner" /></div>;
  const sel = selected && selected !== 'logo' ? design.fields.find(f => f.id === selected) : null;
  const person = previewPerson === 'sample' ? SAMPLE : (people.find(p => String(p.ticket_id) === String(previewPerson)) || SAMPLE);
  const event = info.event || { name: 'Annual Legislative & Business Luncheon', event_date: new Date().toISOString().slice(0, 10) };
  const size = sizeOf(design);

  const save = async () => {
    setSaving(true);
    try { await api.put('/badges/design', { eventId: eventId || null, design }); toast.success(eventId ? 'Saved for this event.' : 'Saved as the design for every event.'); setDirty(false); load(); }
    catch (err) { toast.error(err.response?.data?.error || 'Could not save.'); }
    finally { setSaving(false); }
  };
  const useDefault = async () => {
    if (!window.confirm('Use the standard design for this event instead? This event’s own changes will be removed.')) return;
    try { await api.delete(`/badges/design?eventId=${eventId}`); toast.success('This event now uses the standard design.'); load(); }
    catch (err) { toast.error(err.response?.data?.error || 'Could not change it.'); }
  };
  const onLogo = (file) => {
    if (!file) return;
    if (file.size > 700 * 1024) return toast.error('That picture is too big — please use one under 700 KB (a PNG of the logo is ideal).');
    const rd = new FileReader();
    rd.onload = () => update(d => { d.logo = { src: rd.result, x: d.logo?.x ?? 3, y: d.logo?.y ?? 3, w: d.logo?.w ?? 18 }; return d; });
    rd.readAsDataURL(file);
  };
  // [marker: tag-art v1] your own finished artwork as the tag's background
  const onArt = async (file) => {
    if (!file) return;
    setArtBusy(true);
    try {
      const fd = new FormData(); fd.append('file', file);
      const r = await api.post('/badges/background', fd, { headers: { 'Content-Type': undefined } });
      update(d => { d.bgImage = { src: r.data.url, fit: 'cover', name: file.name }; return d; });
      setArtFit(r.data);
      if (r.data.pages > 1) toast(`Your PDF has ${r.data.pages} pages; the first page is used as the background.`, { icon: 'ℹ️' });
      else toast.success('Artwork added. Drag the names where they should print.');
    } catch (err) { toast.error(err.response?.data?.error || 'That file could not be used.'); }
    finally { setArtBusy(false); }
  };
  const artSizeTip = (() => {
    if (!artFit || !design || !design.bgImage) return null;
    const { w, h } = sizeOf(design);
    if (artFit.widthIn && artFit.heightIn) {
      if (Math.abs(artFit.widthIn - w) < 0.05 && Math.abs(artFit.heightIn - h) < 0.05) return null;
      return { text: `Your PDF is ${artFit.widthIn}" × ${artFit.heightIn}". The tag is set to ${w}" × ${h}".`, w: artFit.widthIn, h: artFit.heightIn };
    }
    if (artFit.ratio && Math.abs(artFit.ratio - w / h) / (w / h) > 0.03) {
      return { text: `Your picture's shape doesn't match a ${w}" × ${h}" tag, so its edges will be trimmed.`, w: Math.round(h * artFit.ratio * 100) / 100, h };
    }
    return null;
  })();
  const addField = (type) => {
    const f = { id: newId(), type, x: 10, y: 45, w: 80, size: type === 'text' ? 12 : 14, bold: false, color: '#111827', align: 'center', font: 'sans', text: type === 'text' ? 'Welcome!' : undefined };
    update(d => { d.fields.push(f); return d; }); setSelected(f.id);
  };

  return (
    <div>
      <div className="page-header">
        <div>
          <h1 className="page-title">Name Tag Design</h1>
          <p className="page-subtitle">
            {eventId
              ? <>For <Link to={`/events/${eventId}`}>{info.event?.name}</Link> — {info.source === 'event' ? 'this event has its own design.' : 'using the standard design; changes you save here apply to this event only.'}</>
              : 'The standard design used for every event (each event can still have its own).'}
          </p>
        </div>
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
          {eventId && info.source === 'event' && canEdit && <button className="btn btn-ghost" onClick={useDefault}>Use the standard design</button>}
          <button className="btn btn-secondary" onClick={() => printBadges(design, [person], event)}>🖨 Test print</button>
          {canEdit && <button className="btn btn-primary" disabled={saving || !dirty} onClick={save}>{saving ? 'Saving…' : dirty ? (eventId ? 'Save for this event' : 'Save standard design') : 'Saved'}</button>}
        </div>
      </div>

      <div style={{ display: 'flex', gap: 20, alignItems: 'flex-start', flexWrap: 'wrap' }}>
        {/* Preview */}
        <div style={{ flex: '1 1 420px' }}>
          <div className="card" style={{ padding: 20, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 12, background: '#eef1f5' }}
               onPointerDown={() => setSelected(null)}>
            <div ref={boxRef}>
              <BadgeView design={design} person={person} event={event} scale={scale} selectedId={selected} onPointerDownItem={canEdit ? onPointerDownItem : undefined} />
            </div>
            <div style={{ fontSize: '0.8rem', color: 'var(--gray-500)' }}>{size.w}" × {size.h}" · drag anything on the tag to move it · tap it to change it</div>
            <div style={{ display: 'flex', gap: 8, alignItems: 'center', fontSize: '0.85rem' }} onPointerDown={e => e.stopPropagation()}>
              Preview with
              <select className="form-select" style={{ width: 'auto', height: 32 }} value={previewPerson} onChange={e => setPreviewPerson(e.target.value)}>
                <option value="sample">A long sample name</option>
                {people.slice(0, 300).map(p => <option key={p.ticket_id} value={p.ticket_id}>{p.first_name} {p.last_name}{p.company ? ` — ${p.company}` : ''}</option>)}
              </select>
            </div>
          </div>
        </div>

        {/* Controls */}
        <div style={{ flex: '0 1 380px', display: 'grid', gap: 14 }}>
          <div className="card" style={{ padding: 14 }}>
            <div style={sec}>Label size</div>
            <select className="form-select" value={design.preset} disabled={!canEdit} onChange={e => update(d => {
              // keep the same look on a bigger or smaller label: text sizes grow/shrink with it
              const before = sizeOf(d);
              d.preset = e.target.value;
              if (e.target.value !== 'custom') d.size = { w: PRESETS[e.target.value].w, h: PRESETS[e.target.value].h }; else d.size = { ...before };
              const after = sizeOf(d); const k = Math.min(after.w / before.w, after.h / before.h);
              if (Math.abs(k - 1) > 0.02) (d.fields || []).forEach(f => { f.size = Math.max(6, Math.round(f.size * k * 2) / 2); });
              return d; })}>
              {Object.entries(PRESETS).map(([k, p]) => <option key={k} value={k}>{p.label}</option>)}
            </select>
            {design.preset === 'custom' && (
              <div style={{ display: 'flex', gap: 8, marginTop: 8, alignItems: 'center', fontSize: '0.85rem' }}>
                Width <input className="form-input" type="number" step="0.05" min="1" max="8.5" style={{ width: 80 }} value={design.size.w} onChange={e => update(d => { d.size.w = parseFloat(e.target.value) || 1; return d; })} />"
                Height <input className="form-input" type="number" step="0.05" min="0.5" max="11" style={{ width: 80 }} value={design.size.h} onChange={e => update(d => { d.size.h = parseFloat(e.target.value) || 1; return d; })} />"
              </div>
            )}
            <div style={{ display: 'flex', gap: 14, marginTop: 10, alignItems: 'center', fontSize: '0.85rem', flexWrap: 'wrap' }}>
              <label style={row}>Background <input type="color" value={design.background || '#ffffff'} disabled={!canEdit} onChange={e => update(d => { d.background = e.target.value; return d; })} /></label>
              <label style={row}><input type="checkbox" checked={!!design.border} disabled={!canEdit} onChange={e => update(d => { d.border = e.target.checked; return d; })} /> Thin border</label>
            </div>
          </div>

          {/* [marker: tag-art v1] */}
          <div className="card" style={{ padding: 14 }}>
            <div style={sec}>Your own artwork</div>
            <p style={{ fontSize: '0.8rem', color: 'var(--gray-500)', margin: '0 0 8px' }}>
              Upload a finished tag design (JPG, PNG or PDF) and the names print on top of it, for full-color printing.
            </p>
            <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
              {canEdit && <label className="btn btn-secondary btn-sm" style={{ cursor: artBusy ? 'wait' : 'pointer' }}>
                {artBusy ? 'Uploading…' : design.bgImage ? 'Replace artwork…' : 'Upload artwork…'}
                <input type="file" accept="image/png,image/jpeg,application/pdf,.pdf" style={{ display: 'none' }} disabled={artBusy} onChange={e => { onArt(e.target.files[0]); e.target.value = ''; }} /></label>}
              {design.bgImage && canEdit && <button className="btn btn-ghost btn-sm" onClick={() => { update(d => { d.bgImage = null; d.plain = false; return d; }); setArtFit(null); }}>Remove</button>}
            </div>
            {design.bgImage && (<>
              {design.bgImage.name && <div style={{ fontSize: '0.78rem', color: 'var(--gray-500)', marginTop: 6 }}>{design.bgImage.name}</div>}
              <label style={{ ...row, marginTop: 8, fontSize: '0.85rem' }}><input type="checkbox" checked={!!design.plain} disabled={!canEdit} onChange={e => update(d => { d.plain = e.target.checked; return d; })} /> Use my artwork only (hide the stripes and logo)</label>
              <label style={{ ...row, marginTop: 4, fontSize: '0.85rem' }}><input type="checkbox" checked={design.bgImage.fit === 'contain'} disabled={!canEdit} onChange={e => update(d => { d.bgImage.fit = e.target.checked ? 'contain' : 'cover'; return d; })} /> Show the whole picture (don't trim the edges)</label>
              {artSizeTip && canEdit && (
                <div style={{ marginTop: 8, padding: '8px 10px', background: '#fff8e1', border: '1px solid #f0d58a', borderRadius: 6, fontSize: '0.8rem' }}>
                  {artSizeTip.text}{' '}
                  <button className="btn btn-ghost btn-sm" style={{ padding: 0 }} onClick={() => { update(d => { d.preset = 'custom'; d.size = { w: artSizeTip.w, h: artSizeTip.h }; return d; }); setArtFit(null); }}>
                    Make the tag {artSizeTip.w}" × {artSizeTip.h}"</button>
                </div>
              )}
            </>)}
          </div>

          <div className="card" style={{ padding: 14 }}>
            <div style={sec}>Logo</div>
            <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
              {canEdit && <label className="btn btn-secondary btn-sm" style={{ cursor: 'pointer' }}>{design.logo ? 'Change logo…' : 'Add logo…'}<input type="file" accept="image/png,image/jpeg,image/svg+xml" style={{ display: 'none' }} onChange={e => onLogo(e.target.files[0])} /></label>}
              {design.logo && canEdit && <button className="btn btn-ghost btn-sm" onClick={() => update(d => { d.logo = null; return d; })}>Remove</button>}
              {!design.logo && info.orgLogoUrl && canEdit && <button className="btn btn-ghost btn-sm" onClick={() => update(d => { d.logo = { src: info.orgLogoUrl, x: 3, y: 3, w: 18 }; return d; })}>Use the NJJBA logo from Settings</button>}
            </div>
            {design.logo && (
              <label style={{ ...row, marginTop: 8, fontSize: '0.85rem' }}>Size
                <input type="range" min="5" max="90" value={design.logo.w} disabled={!canEdit} onChange={e => update(d => { d.logo.w = parseFloat(e.target.value); return d; })} style={{ flex: 1 }} />
              </label>
            )}
          </div>

          <div className="card" style={{ padding: 14 }}>
            <div style={sec}>Color stripes</div>
            {(design.bands || []).map((b, i) => (
              <div key={b.id} style={{ display: 'flex', gap: 6, alignItems: 'center', fontSize: '0.8rem', marginBottom: 6 }}>
                <input type="color" value={b.color} disabled={!canEdit} onChange={e => update(d => { d.bands[i].color = e.target.value; return d; })} />
                from <input className="form-input" type="number" style={{ width: 62, height: 30 }} value={b.y} disabled={!canEdit} onChange={e => update(d => { d.bands[i].y = clamp(parseFloat(e.target.value) || 0, 0, 100); return d; })} />%
                tall <input className="form-input" type="number" style={{ width: 62, height: 30 }} value={b.h} disabled={!canEdit} onChange={e => update(d => { d.bands[i].h = clamp(parseFloat(e.target.value) || 0, 0, 100); return d; })} />%
                {canEdit && <button className="btn btn-ghost btn-sm" onClick={() => update(d => { d.bands.splice(i, 1); return d; })}>✕</button>}
              </div>
            ))}
            {canEdit && <button className="btn btn-ghost btn-sm" onClick={() => update(d => { (d.bands = d.bands || []).push({ id: newId(), y: 90, h: 10, color: '#c8a84b' }); return d; })}>+ Add a stripe</button>}
          </div>

          <div className="card" style={{ padding: 14 }}>
            <div style={sec}>What’s on the tag</div>
            {design.fields.map(f => (
              <div key={f.id} onClick={() => setSelected(f.id)}
                   style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '5px 8px', borderRadius: 6, cursor: 'pointer',
                     background: selected === f.id ? '#eff6ff' : undefined, fontSize: '0.875rem' }}>
                <span>{FIELDS[f.type]}{f.type === 'text' ? `: “${f.text}”` : ''}</span>
                {canEdit && <button className="btn btn-ghost btn-sm" onClick={e => { e.stopPropagation(); update(d => { d.fields = d.fields.filter(x => x.id !== f.id); return d; }); if (selected === f.id) setSelected(null); }}>Remove</button>}
              </div>
            ))}
            {canEdit && (
              <select className="form-select" style={{ marginTop: 8 }} value="" onChange={e => e.target.value && addField(e.target.value)}>
                <option value="">+ Add to the tag…</option>
                {Object.entries(FIELDS).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
              </select>
            )}
          </div>

          {sel && (
            <div className="card" style={{ padding: 14, borderColor: '#93c5fd' }}>
              <div style={sec}>{FIELDS[sel.type]}</div>
              {sel.type === 'text' && <input className="form-input" style={{ marginBottom: 8 }} value={sel.text || ''} disabled={!canEdit} onChange={e => setField(sel.id, 'text', e.target.value)} />}
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8, fontSize: '0.85rem' }}>
                <label>Font
                  <select className="form-select" value={sel.font || 'sans'} disabled={!canEdit} onChange={e => setField(sel.id, 'font', e.target.value)}>
                    <option value="sans">Clean (Helvetica)</option><option value="serif">Classic (Georgia)</option>
                    <option value="rounded">Friendly</option><option value="condensed">Narrow</option>
                  </select>
                </label>
                <label>Size (pt)
                  <input className="form-input" type="number" min="6" max="96" value={sel.size} disabled={!canEdit} onChange={e => setField(sel.id, 'size', clamp(parseFloat(e.target.value) || 12, 6, 96))} />
                </label>
                <label>Color <input type="color" value={sel.color || '#111111'} disabled={!canEdit} onChange={e => setField(sel.id, 'color', e.target.value)} style={{ display: 'block', marginTop: 4 }} /></label>
                <label>Line up
                  <select className="form-select" value={sel.align || 'center'} disabled={!canEdit} onChange={e => setField(sel.id, 'align', e.target.value)}>
                    <option value="left">Left</option><option value="center">Center</option><option value="right">Right</option>
                  </select>
                </label>
              </div>
              <div style={{ display: 'flex', gap: 12, marginTop: 8, fontSize: '0.85rem', flexWrap: 'wrap' }}>
                <label style={row}><input type="checkbox" checked={!!sel.bold} disabled={!canEdit} onChange={e => setField(sel.id, 'bold', e.target.checked)} /> Bold</label>
                <label style={row}><input type="checkbox" checked={!!sel.italic} disabled={!canEdit} onChange={e => setField(sel.id, 'italic', e.target.checked)} /> Italic</label>
                <label style={row}><input type="checkbox" checked={!!sel.upper} disabled={!canEdit} onChange={e => setField(sel.id, 'upper', e.target.checked)} /> CAPITALS</label>
              </div>
              <label style={{ ...row, marginTop: 8, fontSize: '0.85rem' }}>Width
                <input type="range" min="10" max="100" value={sel.w} disabled={!canEdit} onChange={e => setField(sel.id, 'w', parseFloat(e.target.value))} style={{ flex: 1 }} />
              </label>
              <p className="form-hint" style={{ marginBottom: 0 }}>Long names shrink automatically to fit the width.</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
const sec = { fontSize: '0.72rem', fontWeight: 700, textTransform: 'uppercase', color: 'var(--gray-600)', marginBottom: 8 };
const row = { display: 'flex', gap: 6, alignItems: 'center' };
